<!--
 * @Author: caiyuliu
 * @Date: 2025-12-15 09:46:13
 * @LastEditors: caiyuliu
 * @LastEditTime: 2025-12-15 10:04:22
 * @Description: 协议可视化首页
-->
<template>
  <div class="protocol-dashboard">
    <!-- Header -->
    <header class="dashboard-header">
      <h1>📋 安道协议管理中心</h1>
      <p class="header-subtitle">Amdox Protocol Management Center</p>
    </header>

    <!-- Statistics Overview -->
    <section class="stats-section">
      <stat-card title="协议总数" :value="statistics.totalProtocols" icon="📄" color="#4CAF50" />
      <stat-card title="产品数量" :value="statistics.totalProducts" icon="📦" color="#2196F3" />
      <stat-card title="支持语言" :value="statistics.totalLanguages" icon="🌍" color="#FF9800" />
      <stat-card title="最近更新" :value="daysSinceUpdate" icon="🕐" color="#9C27B0" />
    </section>

    <!-- Charts Section -->
    <section class="charts-section">
      <protocol-chart title="协议类型分布" chart-type="pie" :chart-data="typeDistributionData" />
      <protocol-chart title="产品协议数量对比" chart-type="bar" :chart-data="productComparisonData" />
    </section>

    <!-- Search and Filter -->
    <section class="filter-section">
      <search-filter @filter-change="handleFilterChange" />
    </section>

    <!-- Product Cards -->
    <section class="products-section">
      <div v-if="filteredProducts.length === 0" class="empty-state">
        <span class="empty-icon">🔍</span>
        <p>未找到匹配的协议</p>
      </div>
      <div v-else class="product-grid">
        <product-card v-for="product in filteredProducts" :key="product.id" :product="product" />
      </div>
    </section>

    <!-- Version Timeline -->
    <section class="timeline-section" v-if="versions.length > 0">
      <version-timeline :versions="versions" />
    </section>
  </div>
</template>

<script>
import { extractProtocolData } from '@/utils/protocolDataExtractor'
import StatCard from '@/components/StatCard.vue'
import ProtocolChart from '@/components/ProtocolChart.vue'
import ProductCard from '@/components/ProductCard.vue'
import SearchFilter from '@/components/SearchFilter.vue'
import VersionTimeline from '@/components/VersionTimeline.vue'

export default {
  name: 'ProtocolDashboard',
  components: {
    StatCard,
    ProtocolChart,
    ProductCard,
    SearchFilter,
    VersionTimeline
  },
  data() {
    return {
      products: [],
      statistics: {
        totalProtocols: 0,
        totalProducts: 0,
        totalLanguages: 0,
        typeDistribution: {},
        lastUpdate: ''
      },
      versions: [],
      filters: {
        search: '',
        type: '',
        language: ''
      }
    }
  },
  computed: {
    daysSinceUpdate() {
      if (!this.statistics.lastUpdate) return 0
      const lastUpdate = new Date(this.statistics.lastUpdate)
      const today = new Date()
      const diffTime = Math.abs(today - lastUpdate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays
    },
    typeDistributionData() {
      const distribution = this.statistics.typeDistribution || {}
      const colors = {
        免责声明: '#FF9800',
        隐私政策: '#2196F3',
        用户协议: '#4CAF50',
        组织认证: '#9C27B0',
        安装软件: '#607D8B',
        其他: '#757575'
      }

      return Object.entries(distribution).map(([name, value]) => ({
        name,
        value,
        itemStyle: {
          color: colors[name] || '#757575'
        }
      }))
    },
    productComparisonData() {
      const categories = this.products.map(p => p.name)
      const values = this.products.map(p => p.protocols.length)

      return {
        categories,
        values
      }
    },
    filteredProducts() {
      let result = [...this.products]

      // Search filter
      if (this.filters.search) {
        const searchLower = this.filters.search.toLowerCase()
        result = result.filter(product => {
          const nameMatch = product.name.toLowerCase().includes(searchLower) || product.nameEn.toLowerCase().includes(searchLower)
          const protocolMatch = product.protocols.some(p => p.name.toLowerCase().includes(searchLower) || p.typeName.toLowerCase().includes(searchLower))
          return nameMatch || protocolMatch
        })
      }

      // Type filter
      if (this.filters.type) {
        result = result
          .map(product => ({
            ...product,
            protocols: product.protocols.filter(p => p.type === this.filters.type)
          }))
          .filter(product => product.protocols.length > 0)
      }

      // Language filter
      if (this.filters.language) {
        result = result
          .map(product => ({
            ...product,
            protocols: product.protocols.filter(p => p.language.code === this.filters.language)
          }))
          .filter(product => product.protocols.length > 0)
      }

      return result
    }
  },
  created() {
    this.loadProtocolData()
  },
  methods: {
    loadProtocolData() {
      // Get routes from router
      const routes = this.$router.options.routes

      // Extract protocol data
      const data = extractProtocolData(routes)
      console.log('<<< data >>>', data)

      this.products = data.products
      this.statistics = data.statistics
      this.versions = data.versions
    },
    handleFilterChange(filters) {
      this.filters = filters
    }
  }
}
</script>

<style lang="scss" scoped>
.protocol-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 40px 20px;

  .dashboard-header {
    text-align: center;
    margin-bottom: 40px;

    h1 {
      margin: 0 0 8px 0;
      font-size: 36px;
      font-weight: 700;
      color: #333;
      text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
    }

    .header-subtitle {
      margin: 0;
      font-size: 16px;
      color: #666;
      font-weight: 300;
    }
  }

  .stats-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    max-width: 1200px;
    margin: 0 auto 40px;
  }

  .charts-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto 40px;
  }

  .timeline-section {
    max-width: 1200px;
    margin: 0 auto 40px;
  }

  .filter-section {
    max-width: 1200px;
    margin: 0 auto;
  }

  .products-section {
    max-width: 1200px;
    margin: 0 auto;
    margin-bottom: 40px;
    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

      .empty-icon {
        font-size: 64px;
        display: block;
        margin-bottom: 16px;
      }

      p {
        margin: 0;
        font-size: 16px;
        color: #999;
      }
    }

    .product-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
      gap: 24px;
    }
  }
}

@media (max-width: 1024px) {
  .protocol-dashboard {
    .charts-section {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 768px) {
  .protocol-dashboard {
    padding: 20px 12px;

    .dashboard-header {
      margin-bottom: 24px;

      h1 {
        font-size: 28px;
      }

      .header-subtitle {
        font-size: 14px;
      }
    }

    .stats-section {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      margin-bottom: 24px;
    }

    .charts-section {
      gap: 16px;
      margin-bottom: 24px;
    }

    .timeline-section {
      margin-bottom: 24px;
    }

    .products-section .product-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }
}

@media (max-width: 480px) {
  .protocol-dashboard {
    .stats-section {
      grid-template-columns: 1fr;
    }
  }
}
</style>
